import _ from 'lodash'
import { PlatformLogger, PlatformEnvData } from '@wix/thunderbolt-symbols'
import { createLinkUtils, createPromise, logSdkError, logSdkWarning } from '@wix/thunderbolt-commons'
import { getComponentsSDKLoader } from '@wix/thunderbolt-components-registry/getComponentsSDKLoader'
import { ComponentSdksLoader, CoreSdkLoaders } from '../types'
import type { ControllersExports, InitArgs } from './types' // TODO move all core types to ./types
import ClientSpecMapApi from './clientSpecMapService'
import AppsUrlApi from './appsUrlService'
import SessionServiceFactory from './sessionService'
import WixSelector from './wixSelector'
import WixCodeViewerAppUtils from './wixCodeViewerAppUtils'
import { Applications } from './applications'
import modelBuilderFactory from './model'
import { createWixCodeApiFactory } from './createWixCodeSdk'
import createSdkFactoryParams from './createSdkFactoryParams'
import setPropsFactory from './setPropsFactory'
import { ControllerEvents } from './ControllerEvents'
import { DocumentSdkFactory } from './componentsSDK/Document'
import { createPlatformApi } from './appsAPI/platformAPI'
import CommonConfigManager from './commonConfigModule'
import BsiManagerModule from './bsiManagerModule'
import { createWixCodeNamespacesRegistry } from './WixCodeNamespacesRegistry'
import { platformBiLoggerFactory } from './bi/biLoggerFactory'
import { instanceCacheFactory } from './instanceCache'
import { componentSdkStateFactory } from './componentSdkState'
import { ComponentSdksManagerFactory } from './componentSdksManager'
import { RegisterEventFactory } from './createRegisterEvent'
import { PlatformAnimationsAPI } from '../animations'
import { CreateStaticEventsManager } from './staticEventsManager'
import { ModuleLoader } from './loadModules'
import { AppsPublicApiManagerFactory } from './appsPublicApiManager'
import { BuildPlatformUtils } from './buildPlatformUtils'
import { CreateLocationManager } from './locationManager'
import { ViewerPlatformEssentials } from '@wix/fe-essentials-viewer-platform'
import { CreateWarmupDataManager } from './warmupDataManager'
import { CreateConsentPolicyManager } from './consentPolicyManager'

const createProxy = (handle: Function) => new Proxy({}, { get: (__, prop) => handle(prop) })
type PlatformState = {
	loadComponentSdksPromise: Promise<ComponentSdksLoader>
}
export function createPlatformAPI() {
	const { promise: waitForInit, resolver: initDone } = createPromise<PlatformState>()

	return {
		initPlatformOnSite({
			logger,
			moduleLoader,
			componentSdksUrl,
			platformEnvData,
		}: {
			logger: PlatformLogger
			moduleLoader: ModuleLoader
			componentSdksUrl: string | null
			platformEnvData: PlatformEnvData
		}) {
			initDone({
				loadComponentSdksPromise: getComponentsSDKLoader({
					platformEnvData,
					logger,
					loadModule: (url, scriptName) => moduleLoader.AMDLoader(url, scriptName),
					componentSdksUrl,
				}) as any, // TODO: remove `as any` after https://github.com/wix-private/editor-elements/pull/3443 is merged
			})
		},

		async runPlatformOnPage({ bootstrapData, logger, importScripts, moduleLoader, viewerAPI, getModels }: InitArgs) {
			logger.interactionStarted('initialisation')
			const platformState = await waitForInit
			const platformEnvData = bootstrapData.platformEnvData
			const loadComponentSdksPromise = platformState.loadComponentSdksPromise
			const createSdkHandlers = (pageId: string) => createProxy((handlerName: string) => (...args: any) => viewerAPI.invokeSdkHandler(pageId, handlerName, ...args))
			const handlers = createSdkHandlers(bootstrapData.currentPageId) as any
			const modelBuilder = modelBuilderFactory({ bootstrapData, getModels, handlers })
			const modelsApi = await logger.runAsyncAndReport('getAllModels', modelBuilder.getModelApi)

			const clientSpecMapApi = ClientSpecMapApi({ bootstrapData })
			const appsPublicApiManager = AppsPublicApiManagerFactory({ modelsApi, clientSpecMapApi, logger, handlers, bootstrapData, importScripts })
			if (_.isEmpty(modelsApi.getApplications())) {
				appsPublicApiManager.registerPublicApiProvider((appDefinitionId) => {
					appsPublicApiManager.resolvePublicApi(appDefinitionId, null)
				})
				// short circuiting cause all features that have platformApi.hasPlatformOnPage in their predicate were not loaded
				return
			}

			if (platformEnvData.bi.pageData.pageNumber === 1) {
				const applications = modelsApi.getApplications()
				const widgetAppNames = _(applications)
					.entries()
					.flatMap(([app, widgets]) => {
						return _(widgets)
							.values()
							.map((widget) => `${app}_${widget.controllerType}`)
							.value()
					})
					.value()
				handlers.registerWidgets(widgetAppNames)
			}

			const componentSdksManager = ComponentSdksManagerFactory({ loadComponentSdksPromise, modelsApi, logger })
			const sdkInstancesCache = instanceCacheFactory()
			const getCompRefById = (compId: string) => createProxy((functionName: string) => (...args: any) => handlers.invokeCompRefFunction(compId, functionName, args))
			const appsUrlApi = AppsUrlApi({ bootstrapData })
			const sessionService = SessionServiceFactory({ bootstrapData, handlers })
			const controllerEventsFactory = ControllerEvents()
			const componentSdkState = componentSdkStateFactory()
			const commonConfigManager = CommonConfigManager(bootstrapData, createSdkHandlers)
			const bsiManager = BsiManagerModule(commonConfigManager, bootstrapData, createSdkHandlers)
			const linkUtils = createLinkUtils({
				isMobileView: bootstrapData.isMobileView,
				getCompIdByWixCodeNickname: modelsApi.getCompIdByWixCodeNickname,
				getRoleForCompId: modelsApi.getRoleForCompId,
				routingInfo: platformEnvData.router.routingInfo,
				metaSiteId: platformEnvData.location.metaSiteId,
				userFileDomainUrl: platformEnvData.location.userFileDomainUrl,
				isPremiumDomain: platformEnvData.location.isPremiumDomain,
				routersConfig: bootstrapData.platformAPIData.routersConfigMap,
				popupPages: platformEnvData.popups?.popupPages,
				multilingualInfo: platformEnvData.multilingual,
			})
			const wixCodeNamespacesRegistry = createWixCodeNamespacesRegistry()

			const essentials = new ViewerPlatformEssentials({
				metaSiteId: platformEnvData.location.metaSiteId,
				conductedExperiments: {},
				getAppToken(appDefId) {
					return sessionService.getInstance(appDefId)
				},
			})

			const biUtils = platformBiLoggerFactory({ sessionService, factory: essentials.biLoggerFactory })
			const locationManager = CreateLocationManager({ handlers, platformEnvData, bootstrapData })

			const warmupDataManager = CreateWarmupDataManager({ handlers, platformEnvData })
			const consentPolicyManager = CreateConsentPolicyManager({ handlers, platformEnvData })
			const platformUtils = BuildPlatformUtils({
				linkUtils,
				sessionService,
				appsPublicApiManager,
				wixCodeNamespacesRegistry,
				biUtils,
				locationManager,
				essentials,
				warmupDataManager,
				consentPolicyManager,
				clientSpecMapApi,
			})
			const { createSetProps, waitForUpdatePropsPromises, createSetPropsForOOI } = setPropsFactory({ modelsApi, viewerAPI, logger, handlers })
			const registerEventFactory = RegisterEventFactory({ handlers, modelsApi })
			const animationsApi = PlatformAnimationsAPI({ handlers, platformEnvData, modelsApi })
			const { getSdkFactoryParams } = createSdkFactoryParams({
				animationsApi,
				sdkInstancesCache,
				componentSdkState,
				platformUtils,
				viewerAPI,
				modelsApi,
				createSdkHandlers,
				getCompRefById,
				logger,
				createSetProps,
				registerEventFactory,
				platformEnvData,
			})
			const wixSelector = WixSelector({
				bootstrapData,
				modelsApi,
				getSdkFactoryParams,
				controllerEventsFactory,
				sdkInstancesCache,
				componentSdksManager,
				logger,
			})
			const reporter = {
				logSdkError,
				logSdkWarning,
			}
			const controllersExports: ControllersExports = {}

			const AppControllerSdkLoader = async () => {
				const { AppControllerSdk } = await import('./componentsSDK/AppController' /* webpackChunkName: "AppController.corvid" */)
				return AppControllerSdk({ controllersExports, modelsApi, controllerEventsFactory })
			}

			const AppWidgetSdkLoader = async () => {
				const { AppControllerWithChildrenSdk } = await import('./componentsSDK/AppController' /* webpackChunkName: "AppController.corvid" */)
				return AppControllerWithChildrenSdk({ controllersExports, modelsApi, controllerEventsFactory })
			}

			const RepeaterSdkLoader = async () => {
				const { RepeaterSdk } = await import('./componentsSDK/repeaters' /* webpackChunkName: "Repeater.corvid" */)
				return RepeaterSdk({
					modelsApi,
					viewerAPI,
					wixSelector,
					reporter,
					sdkInstancesCache,
					componentSdkState,
				})
			}

			const DocumentSdkLoader = async () => Promise.resolve(DocumentSdkFactory({ modelsApi, wixSelector }))

			const coreSdks: CoreSdkLoaders = {
				AppController: AppControllerSdkLoader,
				AppWidget: AppWidgetSdkLoader,
				TPAWidget: AppControllerSdkLoader,
				TPASection: AppControllerSdkLoader,
				TPAMultiSection: AppControllerSdkLoader,
				TPAGluedWidget: AppControllerSdkLoader,
				tpaWidgetNative: AppControllerSdkLoader,
				Repeater: RepeaterSdkLoader,
				Document: DocumentSdkLoader,
			}
			componentSdksManager.fetchComponentsSdks(coreSdks)
			const staticEventsManager = CreateStaticEventsManager({ modelsApi, controllerEventsFactory, wixSelector, logger })
			const wixCodeViewerAppUtils = WixCodeViewerAppUtils({ bootstrapData, staticEventsManager })
			const wixCodeApiFactory = createWixCodeApiFactory({
				bootstrapData,
				wixCodeViewerAppUtils,
				modelsApi,
				platformUtils,
				createSdkHandlers,
				platformEnvData,
				logger,
			})

			const createPlatformApiForApp = createPlatformApi(bootstrapData, platformUtils, handlers)

			const { startApplications } = Applications({
				appsPublicApiManager,
				platformUtils,
				clientSpecMapApi,
				appsUrlApi,
				modelsApi,
				bootstrapData,
				importScripts,
				moduleLoader,
				wixCodeViewerAppUtils,
				wixSelector,
				logger,
				wixCodeApiFactory,
				createSetPropsForOOI,
				waitForUpdatePropsPromises,
				controllersExports,
				createPlatformApiForApp,
				bsiManager,
				essentials,
			})
			logger.interactionEnded('initialisation')

			await logger.runAsyncAndReport('startApplications', startApplications)
			// calling it here because we need to run all the applications, register the controllers APIs, run and finish all PageReady/OnReady, before executing any static events handlers.
			// some handlers may depends on the apis being registered and onReady been called,
			staticEventsManager.triggerStaticEventsHandlers() // TODO do we need to run this is SSR?
		},
	}
}
